import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Radio, Plus, CalendarClock, User, Bell, BellRing } from "lucide-react";

const canHost = (user) => user && (["agent", "admin"].includes(user.role) || user.live_access);

export default function LiveSales() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [myReminders, setMyReminders] = useState([]);

  const refresh = () => api.get("/live/sessions").then(r => setSessions(r.data)).catch(() => {});
  useEffect(() => {
    refresh();
    if (user) api.get("/live/my-reminders").then(r => setMyReminders(r.data)).catch(() => {});
  }, [user]);

  const toggleReminder = async (sid) => {
    if (!user) { toast.error("Connecte-toi pour activer le rappel"); return; }
    try {
      const { data } = await api.post(`/live/sessions/${sid}/remind`);
      setMyReminders(prev => data.subscribed ? [...prev, sid] : prev.filter(i => i !== sid));
      toast.success(data.subscribed ? "🔔 Rappel activé — on te prévient 10 min avant !" : "Rappel désactivé");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!sessions) return <div className="p-20 text-center">Chargement…</div>;

  const live = sessions.filter(s => s.status === "live");
  const upcoming = sessions.filter(s => s.status === "scheduled");
  const ended = sessions.filter(s => s.status === "ended");

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="live-sales-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="label flex items-center gap-2 text-[color:var(--color-coral)]">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[color:var(--color-coral)] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[color:var(--color-coral)]"></span>
            </span>
            En direct
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">Brocante Live</h1>
          <p className="text-sm text-[color:var(--color-text-muted)] mt-2 max-w-lg">Ventes en direct animées par nos vendeurs sélectionnés et agents FRIPRI. Enchères flash & bonnes affaires en volume.</p>
        </div>
        {canHost(user) && (
          <button onClick={() => setCreateOpen(true)} className="btn btn-mint" data-testid="create-live-session-btn">
            <Plus size={16} strokeWidth={3}/> Créer une session
          </button>
        )}
      </div>

      {live.length > 0 && (
        <Section title="🔴 En direct maintenant" testid="live-now-section">
          {live.map(s => <SessionCard key={s.session_id} s={s} highlight/>)}
        </Section>
      )}

      <Section title="À venir" testid="upcoming-section">
        {upcoming.length === 0 ? <Empty text="Aucune session programmée. Reviens bientôt !"/> : upcoming.map(s => (
          <SessionCard key={s.session_id} s={s} reminded={myReminders.includes(s.session_id)} onRemind={() => toggleReminder(s.session_id)}/>
        ))}
      </Section>

      {ended.length > 0 && (
        <Section title="Sessions terminées" testid="ended-section">
          {ended.slice(0, 6).map(s => <SessionCard key={s.session_id} s={s}/>)}
        </Section>
      )}

      {createOpen && <CreateSessionModal user={user} onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); refresh(); }}/>}
    </div>
  );
}

function Section({ title, children, testid }) {
  return (
    <div className="mb-10" data-testid={testid}>
      <h2 className="text-lg font-black tracking-tight mb-4">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="col-span-full text-center py-12 border-2 border-dashed border-black text-sm text-[color:var(--color-text-muted)]">{text}</div>;
}

function SessionCard({ s, highlight, reminded, onRemind }) {
  return (
    <Link to={`/brocante-live/${s.session_id}`} className={`block p-5 border-2 border-black card-hover relative ${highlight ? "bg-black text-white" : "bg-white"}`} data-testid={`session-card-${s.session_id}`}>
      <div className="flex items-center justify-between mb-3">
        {s.status === "live" ? (
          <span className="pill pill-coral inline-flex items-center gap-1.5"><Radio size={10}/> EN DIRECT</span>
        ) : s.status === "scheduled" ? (
          <span className="pill pill-mint inline-flex items-center gap-1.5"><CalendarClock size={10}/> À venir</span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span className="pill">Terminée</span>
            {s.replay_path && <span className="pill pill-coral text-[10px]" data-testid={`replay-badge-${s.session_id}`}>▶ Replay disponible</span>}
          </span>
        )}
        {s.scheduled_at && s.status === "scheduled" && (
          <span className="text-xs font-bold">{new Date(s.scheduled_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
        )}
      </div>
      <h3 className="font-black text-xl tracking-tight mb-1 truncate">{s.title}</h3>
      {s.description && <p className={`text-xs mb-3 line-clamp-2 ${highlight ? "text-white/70" : "text-[color:var(--color-text-muted)]"}`}>{s.description}</p>}
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs font-bold inline-flex items-center gap-1.5 ${highlight ? "text-[color:var(--color-mint)]" : ""}`}><User size={12}/> Animé par {s.host_name || "FRIPRI"}</p>
        {s.status === "scheduled" && onRemind && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemind(); }}
            className={`btn btn-sm inline-flex items-center gap-1.5 shrink-0 ${reminded ? "bg-[color:var(--color-yellow)] border-2 border-black" : "border-2 border-black bg-white hover:bg-[color:var(--color-yellow)]"}`}
            data-testid={`remind-btn-${s.session_id}`}
          >
            {reminded ? <BellRing size={13}/> : <Bell size={13}/>} {reminded ? "Rappel activé" : "Me rappeler"}
          </button>
        )}
      </div>
    </Link>
  );
}

function CreateSessionModal({ user, onClose, onCreated }) {
  const [form, setForm] = useState({ title: "", description: "", video_url: "", scheduled_at: "", host_email: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.host_email) delete payload.host_email;
      if (!payload.scheduled_at) delete payload.scheduled_at;
      else payload.scheduled_at = new Date(payload.scheduled_at).toISOString();
      await api.post("/live/sessions", payload);
      toast.success("Session créée ✓");
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="create-session-modal">
      <form onClick={e => e.stopPropagation()} onSubmit={submit} className="bg-white border-2 border-black w-full max-w-md p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-2xl font-black tracking-tighter">Nouvelle session live</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none">×</button>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Titre</label>
          <input className="field" required minLength={3} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brocante vintage du dimanche" data-testid="session-title-input"/>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Description (optionnel)</label>
          <textarea className="field" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="session-desc-input"/>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Lien vidéo YouTube / Twitch (optionnel)</label>
          <input className="field" value={form.video_url} onChange={e => setForm({ ...form, video_url: e.target.value })} placeholder="https://youtube.com/live/… ou https://twitch.tv/…" data-testid="session-video-input"/>
        </div>
        <div>
          <label className="label font-bold mb-1 block">Date programmée (optionnel)</label>
          <input className="field" type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} data-testid="session-date-input"/>
        </div>
        {user?.role === "admin" && (
          <div>
            <label className="label font-bold mb-1 block">Email de l'hôte (admin — optionnel)</label>
            <input className="field" type="email" value={form.host_email} onChange={e => setForm({ ...form, host_email: e.target.value })} placeholder="vendeur@exemple.fr" data-testid="session-host-input"/>
          </div>
        )}
        <button type="submit" disabled={saving} className="btn btn-primary w-full" data-testid="session-submit-btn">
          {saving ? "Création…" : "Créer la session →"}
        </button>
      </form>
    </div>
  );
}
